import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * @function useInput
 * @desc this hook provides a simple input state updated with the content of an event.target.value
 * @desc also, a callback function call can be debounced after the state update
 * @param {any} initInput - initial input value
 * @param {function} [callbackFn] - optional function to call after the state update
 * @param {number} [callbackDelay] - delay before triggereing the callbackFn
 * @returns {Array} - an array that contains input, update
 */
export default (
  initInput = '',
  callbackFn?: (value: string) => void,
  callbackDelay = 0
): [string, (e: React.ChangeEvent<HTMLInputElement>) => void] => {
  // State and setter for input
  const [input, setInput] = useState(initInput);
  const [initialValue, setInitialValue] = useState(initInput);

  // ? Re-sync input value with initial value if it changes
  useEffect(() => {
    if (initInput !== initialValue) {
      setInput(initInput);
      setInitialValue(initInput);
    }
  }, [initInput, initialValue]);

  // callback
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const update = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newInputValue = e.target.value;
      // set the state new value
      setInput(newInputValue);

      // if a callback has been provided, call it after a potential delay
      if (callbackFn) {
        if (!callbackDelay) {
          callbackFn(newInputValue);
          return;
        }

        // Cancel the previous timeout if already set
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        // then call callback fn after a delay, unless something is changed again
        timeoutRef.current = setTimeout(() => {
          callbackFn(newInputValue);
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
        }, callbackDelay);
      }
    },
    [callbackFn, callbackDelay]
  );

  return [input, update];
};
